import type {
  CartItem,
  TicketConstraint,
  TicketConstraintGroup,
  TicketTypeV2,
  ValidationConditionContext,
  ValidationError,
  ValidationResult,
} from "./types";

function getCartQuantity(cart: CartItem[], ticketTypeId: string | undefined): number {
  if (!ticketTypeId) return 0;
  const item = cart.find((c) => c.ticketTypeId === ticketTypeId);
  return item?.quantity ?? 0;
}

function getCartQuantityForAny(cart: CartItem[], ticketTypeIds: string[] | undefined): number {
  if (!ticketTypeIds || ticketTypeIds.length === 0) return 0;
  return ticketTypeIds.reduce((sum, id) => sum + getCartQuantity(cart, id), 0);
}

function resolveConstraintGroups(ticketType: TicketTypeV2): TicketConstraintGroup[] {
  if (ticketType.constraintGroups && ticketType.constraintGroups.length > 0) {
    return ticketType.constraintGroups;
  }
  const legacy: TicketConstraint[] = ticketType.constraints ?? [];
  return legacy.map((c, idx) => ({ id: `legacy-${idx}`, constraints: [c] }));
}

type CheckParams = {
  constraint: TicketTypeV2["constraints"][number];
  item: CartItem;
  cart: CartItem[];
  ticketType: TicketTypeV2;
  ticketTypes: TicketTypeV2[];
  totalTickets: number;
  soldOutTicketIds?: string[];
  selectedSessions?: Array<{ date: Date | string }>;
  priorPurchasedTicketIds?: string[];
};

function checkConstraint(params: CheckParams): { passes: boolean; message?: string } {
  const { constraint, item, cart, ticketType, ticketTypes, totalTickets, soldOutTicketIds, selectedSessions, priorPurchasedTicketIds } = params;

  switch (constraint.type) {
    case "REQUIRES_TICKET": {
      const targetQty = getCartQuantity(cart, constraint.targetTicketId);
      if (targetQty >= constraint.minQuantity) return { passes: true };
      const targetTicket = ticketTypes.find((t) => t.id === constraint.targetTicketId);
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} requires at least ${constraint.minQuantity} ${targetTicket?.name ?? "required"} ticket(s)`,
      };
    }
    case "REQUIRES_ANY_OF": {
      const anyQty = getCartQuantityForAny(cart, constraint.targetTicketIds);
      if (anyQty >= constraint.minQuantity) return { passes: true };
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} requires at least ${constraint.minQuantity} other ticket(s)`,
      };
    }
    case "MAX_PER_OTHER": {
      const otherQty = getCartQuantity(cart, constraint.targetTicketId);
      const maxAllowed = otherQty * (constraint.maxQuantity ?? 1);
      if (item.quantity <= maxAllowed) return { passes: true };
      const targetTicket = ticketTypes.find((t) => t.id === constraint.targetTicketId);
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `Maximum ${constraint.maxQuantity} ${ticketType.name} ticket(s) per ${targetTicket?.name ?? "other"} ticket`,
      };
    }
    case "MUTUALLY_EXCLUSIVE": {
      const conflictQty = getCartQuantity(cart, constraint.targetTicketId);
      if (conflictQty === 0) return { passes: true };
      const targetTicket = ticketTypes.find((t) => t.id === constraint.targetTicketId);
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} cannot be purchased together with ${targetTicket?.name ?? "another ticket"}`,
      };
    }
    case "MIN_TOTAL_TICKETS": {
      if (totalTickets >= constraint.minQuantity) return { passes: true };
      return {
        passes: false,
        message: constraint.errorMessage ?? `At least ${constraint.minQuantity} ticket(s) are required`,
      };
    }
    case "MAX_TOTAL_TICKETS": {
      const maxAllowed = constraint.maxQuantity ?? constraint.minQuantity;
      if (totalTickets <= maxAllowed) return { passes: true };
      return {
        passes: false,
        message: constraint.errorMessage ?? `Maximum ${maxAllowed} ticket(s) per order`,
      };
    }
    case "EXACT_MULTIPLES": {
      const multiple = constraint.multipleOf ?? 1;
      if (multiple <= 1 || item.quantity % multiple === 0) return { passes: true };
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} must be purchased in multiples of ${multiple}`,
      };
    }
    case "AVAILABLE_IF_SOLD_OUT": {
      const gateTicketId = constraint.targetTicketId;
      if (!gateTicketId || !soldOutTicketIds || soldOutTicketIds.includes(gateTicketId)) {
        return { passes: true };
      }
      const gateTicket = ticketTypes.find((t) => t.id === gateTicketId);
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} is only available after ${gateTicket?.name ?? "another ticket"} is sold out`,
      };
    }
    case "SESSION_BUNDLE": {
      const minSessions = constraint.minSessions ?? 1;
      const maxSessions = constraint.maxSessions ?? null;
      const timeWindowDays = constraint.timeWindowDays ?? 30;
      if (!selectedSessions || selectedSessions.length < minSessions) {
        return {
          passes: false,
          message:
            constraint.errorMessage ??
            `Select at least ${minSessions} sessions happening in the next ${timeWindowDays} days to unlock this ticket. Please go back to select more dates.`,
        };
      }
      // Sessions are shared across ticket quantity (qty 2 = 2 people on the
      // same dates), so the cap does not scale with quantity.
      if (maxSessions !== null && selectedSessions.length > maxSessions) {
        return {
          passes: false,
          message:
            constraint.errorMessage ??
            `${ticketType.name} covers up to ${maxSessions} sessions — you have ${selectedSessions.length} dates selected. Please go back and reduce your dates, or choose a ticket that covers more sessions.`,
        };
      }
      const dates = selectedSessions.map((s) => new Date(s.date).getTime()).sort((a, b) => a - b);
      const spanDays = (dates[dates.length - 1]! - dates[0]!) / (1000 * 60 * 60 * 24);
      if (spanDays > timeWindowDays) {
        return {
          passes: false,
          message:
            constraint.errorMessage ??
            `All selected sessions must be within ${timeWindowDays} days of each other`,
        };
      }
      return { passes: true };
    }
    case "REQUIRES_PRIOR_PURCHASE": {
      const targets = constraint.targetTicketIds ?? [];
      // Misconfigured (no targets) fails open like AVAILABLE_IF_SOLD_OUT's
      // missing target; the dashboard write path prevents persisting one.
      if (targets.length === 0) return { passes: true };
      const purchased = priorPurchasedTicketIds ?? [];
      if (targets.some((id) => purchased.includes(id))) return { passes: true };
      const names = targets
        .map((id) => ticketTypes.find((t) => t.id === id)?.name)
        .filter((n): n is string => !!n);
      return {
        passes: false,
        message:
          constraint.errorMessage ??
          `${ticketType.name} — Available after you've booked ${names.length > 0 ? names.join(" or ") : "the required ticket"}`,
      };
    }
    default:
      return { passes: true };
  }
}

export function validateTicketConstraints(
  cart: CartItem[],
  ticketTypes: TicketTypeV2[],
  soldOutTicketIds?: string[],
  selectedSessions?: Array<{ date: Date | string }>,
  validationCtx?: ValidationConditionContext,
): ValidationResult {
  const errors: ValidationError[] = [];
  const totalTickets = cart.reduce((sum, item) => sum + item.quantity, 0);
  const activeCartItems = cart.filter((item) => item.quantity > 0);

  for (const item of activeCartItems) {
    const ticketType = ticketTypes.find((t) => t.id === item.ticketTypeId);
    if (!ticketType) continue;

    const groups = resolveConstraintGroups(ticketType);

    if (groups.length > 0) {
      const groupFailureMessages: string[] = [];
      let anyGroupPassed = false;

      for (const group of groups) {
        if (group.constraints.length === 0) {
          anyGroupPassed = true;
          break;
        }
        const innerFailures: string[] = [];
        let allInnerPassed = true;
        for (const constraint of group.constraints) {
          const result = checkConstraint({
            constraint,
            item,
            cart,
            ticketType,
            ticketTypes,
            totalTickets,
            soldOutTicketIds,
            selectedSessions,
            priorPurchasedTicketIds: validationCtx?.priorPurchasedTicketIds,
          });
          if (!result.passes) {
            allInnerPassed = false;
            if (result.message) innerFailures.push(result.message);
          }
        }
        if (allInnerPassed) {
          anyGroupPassed = true;
          break;
        }
        if (innerFailures.length > 0) {
          groupFailureMessages.push(
            innerFailures.length > 1 ? innerFailures.join(" AND ") : innerFailures[0]!,
          );
        }
      }

      if (!anyGroupPassed) {
        const firstGroupType = groups.find((g) => g.constraints.length > 0)?.constraints[0]?.type;
        errors.push({
          ticketTypeId: item.ticketTypeId,
          constraintType: firstGroupType ?? "REQUIRES_TICKET",
          message:
            groupFailureMessages.length > 1
              ? `${ticketType.name} cannot be added — none of the rules are satisfied: ${groupFailureMessages.join(" OR ")}`
              : (groupFailureMessages[0] ?? `${ticketType.name} is not available`),
        });
      }
    }

    if (ticketType.minTicketsPerOrder > 1 && item.quantity < ticketType.minTicketsPerOrder) {
      errors.push({
        ticketTypeId: item.ticketTypeId,
        constraintType: "REQUIRES_TICKET",
        message: `Minimum ${ticketType.minTicketsPerOrder} ${ticketType.name} ticket(s) per order`,
      });
    }

    if (ticketType.maxTicketsPerOrder && item.quantity > ticketType.maxTicketsPerOrder) {
      errors.push({
        ticketTypeId: item.ticketTypeId,
        constraintType: "REQUIRES_TICKET",
        message: `Maximum ${ticketType.maxTicketsPerOrder} ${ticketType.name} ticket(s) per order`,
      });
    }
  }

  const uniqueErrors = errors.filter(
    (error, index, self) =>
      index === self.findIndex((e) => e.ticketTypeId === error.ticketTypeId && e.message === error.message),
  );

  return {
    isValid: uniqueErrors.length === 0,
    errors: uniqueErrors,
  };
}

export type PriorPurchaseDisplayState =
  | { state: "visible" }
  | { state: "locked"; message: string }
  | { state: "hidden" };

/**
 * Static (cart-independent) prior-purchase eligibility. Cart-dependent rules
 * are treated as passing, so a non-"visible" result means NO cart can ever
 * make this ticket bookable for this buyer — safe to hide/lock/enforce
 * without cart context. "hidden" only when every blocking rule opts in.
 */
export function evaluatePriorPurchaseDisplayState(
  ticketType: TicketTypeV2,
  ticketTypes: TicketTypeV2[],
  priorPurchasedTicketIds: string[],
): PriorPurchaseDisplayState {
  const groups = resolveConstraintGroups(ticketType);
  if (groups.length === 0) return { state: "visible" };

  const blockingRules: TicketConstraint[] = [];
  for (const group of groups) {
    const unmet = group.constraints.filter(
      (c) =>
        c.type === "REQUIRES_PRIOR_PURCHASE" &&
        (c.targetTicketIds ?? []).length > 0 &&
        !(c.targetTicketIds ?? []).some((id) => priorPurchasedTicketIds.includes(id)),
    );
    if (unmet.length === 0) return { state: "visible" };
    blockingRules.push(...unmet);
  }

  if (blockingRules.every((c) => c.unmetDisplay === "hidden")) return { state: "hidden" };

  const first = blockingRules[0]!;
  const names = (first.targetTicketIds ?? [])
    .map((id) => ticketTypes.find((t) => t.id === id)?.name)
    .filter((n): n is string => !!n);
  return {
    state: "locked",
    message:
      first.errorMessage ??
      `Available after you've booked ${names.length > 0 ? names.join(" or ") : "the required ticket"}`,
  };
}
